/**
 * useCountUp — anima un número desde 0 hasta el valor objetivo cuando el
 * elemento entra en el viewport. Respeta `prefers-reduced-motion`.
 *
 * Uso:
 *   const { ref, display } = useCountUp(120, { duration: 1500 });
 *   <div ref={ref}>{display}</div>
 *
 * Si el `target` no es numérico (ej. `"+25"` ya formateado), devolverá el
 * valor tal cual sin animar.
 */
import { useEffect, useRef, useState } from 'react';

import { useReducedMotion } from './useMediaQuery';

export interface CountUpOptions {
  /** Milisegundos que dura la animación. Default 1400. */
  duration?: number;
  /** Cuánto debe estar visible el elemento antes de disparar. Default 0.3. */
  threshold?: number;
}

/** Easing easeOutCubic — arranca rápido, frena al final. */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function useCountUp(target: number | string, options: CountUpOptions = {}) {
  const { duration = 1400, threshold = 0.3 } = options;
  const ref = useRef<HTMLSpanElement | null>(null);
  const reduced = useReducedMotion();
  const numericTarget = typeof target === 'number' ? target : Number.parseInt(target, 10);
  const isNumeric = Number.isFinite(numericTarget);

  const [value, setValue] = useState<number>(isNumeric ? 0 : NaN);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isNumeric) return;
    if (reduced) {
      setValue(numericTarget);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            observer.disconnect();
            const start = performance.now();
            const tick = (now: number) => {
              const elapsed = now - start;
              const progress = Math.min(1, elapsed / duration);
              setValue(Math.round(easeOutCubic(progress) * numericTarget));
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            break;
          }
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [numericTarget, isNumeric, duration, threshold, reduced]);

  const display = isNumeric ? String(value) : String(target);
  return { ref, display };
}
